import { useTranslation } from 'react-i18next';

import AddLine from '@/assets/icons/ri_add_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { OverviewCage, OverviewLocation } from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import FishGroupStatusBadge from './FishGroupStatusBadge';

import styles from './FishGroupsOverview.module.scss';

const PLACEHOLDER = '—';
const ROW_HEIGHT = 44;

interface Props {
    location: OverviewLocation;
    onOpenFishGroup: (fishGroupId: number) => void;
    onCreateFishGroup: (cage: { id: number; name: string; locationName: string }) => void;
    onOpenTimeline: (cage: { id: number; name: string }) => void;
}

export default function LocationFishGroupsTable(props: Props) {
    const { location, onOpenFishGroup, onCreateFishGroup, onOpenTimeline } = props;
    const { t } = useTranslation('fishGroups');

    const columns: DataTableColumnDef<OverviewCage>[] = [
        {
            id: 'cage',
            header: t('overview.col.cage'),
            cell: ({ row }) => row.original.cageName,
        },
        {
            id: 'bioscope',
            header: t('overview.col.bioscope'),
            cell: ({ row }) => <BioscopeCell cage={row.original} />,
        },
        {
            id: 'fishGroupName',
            header: t('overview.col.fishGroupName'),
            meta: { width: '1.5fr' },
            cell: ({ row }) => (
                <FishGroupNameCell cage={row.original} onOpenFishGroup={onOpenFishGroup} />
            ),
        },
        {
            id: 'fishGroupStatus',
            header: t('overview.col.fishGroupStatus'),
            cell: ({ row }) => <FishGroupStatusCell cage={row.original} />,
        },
        {
            id: 'lifecycleStartedAt',
            header: t('overview.col.lifecycleStarted'),
            cell: ({ row }) => <LifecycleStartedCell cage={row.original} />,
        },
        {
            id: 'actions',
            header: '',
            meta: { width: '0.9fr', align: 'right' },
            cell: ({ row }) => (
                <ActionsCell
                    cage={row.original}
                    locationName={location.locationName}
                    onCreateFishGroup={onCreateFishGroup}
                    onOpenTimeline={onOpenTimeline}
                />
            ),
        },
    ];

    const cages = location.cages;

    return (
        <section className={styles.locationSection}>
            <header className={styles.locationHeader}>
                <h5 className={styles.locationTitle}>{location.locationName}</h5>
            </header>
            <DataTable
                data={cages}
                columns={columns}
                getRowId={(cage) => String(cage.cageId)}
                layout="grid"
                enableSorting={false}
                rowHeight={ROW_HEIGHT}
                getRowProps={() => ({ disableHover: true })}
                emptyState={t('overview.noCages')}
            />
        </section>
    );
}

function Placeholder() {
    return <span className={styles.placeholder}>{PLACEHOLDER}</span>;
}

function BioscopeCell(props: { cage: OverviewCage }) {
    const { cage } = props;
    return cage.activeBioscopes.length > 0 ? (
        <>{cage.activeBioscopes.join(', ')}</>
    ) : (
        <Placeholder />
    );
}

function FishGroupNameCell(props: {
    cage: OverviewCage;
    onOpenFishGroup: (fishGroupId: number) => void;
}) {
    const fg = props.cage.activeFishGroup;
    if (!fg) return <Placeholder />;
    return (
        <Button variant="link" type="button" onClick={() => props.onOpenFishGroup(fg.id)}>
            {fg.name}
        </Button>
    );
}

function FishGroupStatusCell(props: { cage: OverviewCage }) {
    const fg = props.cage.activeFishGroup;
    if (!fg) return <Placeholder />;
    return <FishGroupStatusBadge status={fg.status} />;
}

function LifecycleStartedCell(props: { cage: OverviewCage }) {
    const fg = props.cage.activeFishGroup;
    return fg?.lifecycleStartedAt ? (
        <>{formatDateTimeWorded(fg.lifecycleStartedAt, 'minute')}</>
    ) : (
        <Placeholder />
    );
}

interface ActionsCellProps {
    cage: OverviewCage;
    locationName: string;
    onCreateFishGroup: (cage: { id: number; name: string; locationName: string }) => void;
    onOpenTimeline: (cage: { id: number; name: string }) => void;
}

function ActionsCell(props: ActionsCellProps) {
    const { cage, locationName, onCreateFishGroup, onOpenTimeline } = props;
    const { t } = useTranslation('fishGroups');
    const canCreate = cage.activeFishGroup == null;

    return (
        <div className={styles.actionsCell}>
            <Button
                variant="secondary"
                size="small"
                type="button"
                title={t('overview.timelineButton')}
                aria-label={t('overview.timelineButton')}
                onClick={() => onOpenTimeline({ id: cage.cageId, name: cage.cageName })}>
                {t('overview.timeline')}
            </Button>
            {canCreate && (
                <Button
                    variant="secondary"
                    size="small"
                    type="button"
                    icon={<SvgIcon icon={AddLine} />}
                    onClick={() =>
                        onCreateFishGroup({ id: cage.cageId, name: cage.cageName, locationName })
                    }>
                    {t('overview.createButton')}
                </Button>
            )}
        </div>
    );
}
